import { AxiosResponse } from 'axios';
import axiosConfig from 'src/Resources/axiosConfig';
import { UnixTime } from 'src/dataCache/time';

// History of the `/Config/CurrentPrice` value (CHF/kWh) for the Configuration tab chart.
// The backend GetCurrentPriceHistory endpoint reads the per-15-minute price from S3
// chunks server-side and returns ready-to-plot points, so the browser makes one call.

export interface CurrentPriceSeries {
  // [timestampMs, price] — timestampMs is local-time-shifted like the other charts
  data: [number, number][];
  min: number;
  max: number;
}

interface PricePointDto {
  timestamp: number; // unix seconds (UTC)
  price: number; // CHF/kWh
}

const EMPTY: CurrentPriceSeries = { data: [], min: 0, max: 0 };

// Match the other dashboards: render unix-second timestamps shifted into the
// browser's local zone so the x-axis reads as local time.
const toLocalMs = (ticks: number): number => {
  const d = new Date(ticks * 1000);
  d.setHours(d.getHours() - d.getTimezoneOffset() / 60);
  return d.getTime();
};

export const fetchCurrentPriceHistory = async (
  id: number,
  start: UnixTime,
  end: UnixTime
): Promise<CurrentPriceSeries> => {
  let points: PricePointDto[] = [];
  try {
    const res: AxiosResponse<PricePointDto[]> = await axiosConfig.get(
      `/GetCurrentPriceHistory?id=${id}&start=${start.ticks}&end=${end.ticks}`
    );
    points = res.data ?? [];
  } catch {
    return EMPTY;
  }

  const data: [number, number][] = [];
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  points.forEach((p) => {
    if (!p || p.price === undefined || p.price === null || Number.isNaN(p.price)) {
      return;
    }
    data.push([toLocalMs(p.timestamp), p.price]);
    if (p.price < min) min = p.price;
    if (p.price > max) max = p.price;
  });

  if (data.length === 0) return EMPTY;
  data.sort((a, b) => a[0] - b[0]);
  return { data, min, max };
};
